import { useEffect, useRef } from 'react';
import { reportError } from '../utils/sentry';
import { canvasError } from '../utils/sentryBreadcrumbs';

/**
 * Custom hook that monitors the Haxe/OpenFL canvas inside #lab-div-id
 * for crashes that React error boundaries cannot catch.
 *
 * Strategies:
 *   1. window.onerror — catches uncaught JS errors from Haxe engine
 *   2. window.onunhandledrejection — catches uncaught promise rejections
 *   3. MutationObserver — detects if the canvas element disappears
 *   4. Periodic health check — verifies canvas still has dimensions
 *
 * When a crash is detected, injects a styled recovery overlay into #lab-div-id.
 *
 * @param {boolean} isLabLoaded - Whether the lab has finished loading
 */
export default function useLabCanvasGuard(isLabLoaded) {
  const overlayShownRef = useRef(false);
  const healthIntervalRef = useRef(null);
  const observerRef = useRef(null);
  const prevErrorHandlerRef = useRef(null);
  const prevRejectionHandlerRef = useRef(null);

  useEffect(() => {
    if (!isLabLoaded) return;

    const labDiv = document.getElementById('lab-div-id');
    if (!labDiv) return;

    /**
     * Inject a recovery overlay into the lab div
     */
    function showCrashOverlay(reason) {
      if (overlayShownRef.current) return;
      overlayShownRef.current = true;

      console.error('[LabCanvasGuard] Lab crash detected:', reason);

      // Report to Sentry
      var reportErr = new Error('[LabCanvasGuard] ' + reason);
      reportErr.name = 'LabCanvasGuardError';
      reportError(reportErr, { guard: 'LabCanvasGuard', reason: reason });

      // Clean up health check since we've detected the issue
      if (healthIntervalRef.current) {
        clearInterval(healthIntervalRef.current);
        healthIntervalRef.current = null;
      }

      const overlay = document.createElement('div');
      overlay.id = 'lab-crash-overlay';
      overlay.style.cssText = [
        'position: absolute',
        'top: 0',
        'left: 0',
        'right: 0',
        'bottom: 0',
        'display: flex',
        'flex-direction: column',
        'align-items: center',
        'justify-content: center',
        'background-color: var(--card, rgba(30, 30, 30, 0.95))',
        'z-index: 10000',
        'text-align: center',
        'padding: 32px',
        'font-family: var(--font-sans, "Libre Baskerville", serif)'
      ].join('; ');

      const icon = document.createElement('div');
      icon.style.cssText = [
        'width: 48px',
        'height: 48px',
        'border-radius: 50%',
        'background-color: var(--destructive, #dc2626)',
        'display: flex',
        'align-items: center',
        'justify-content: center',
        'margin-bottom: 16px',
        'font-size: 22px',
        'font-weight: bold',
        'color: var(--destructive-foreground, #fff)'
      ].join('; ');
      icon.textContent = '!';

      const title = document.createElement('p');
      title.style.cssText = [
        'font-size: 16px',
        'font-weight: 600',
        'color: var(--foreground, #e8e0d8)',
        'margin: 0 0 8px 0'
      ].join('; ');
      title.textContent = 'The lab encountered an error';

      const subtitle = document.createElement('p');
      subtitle.style.cssText = [
        'font-size: 13px',
        'color: var(--muted-foreground, #999)',
        'margin: 0 0 20px 0',
        'max-width: 360px'
      ].join('; ');
      subtitle.textContent = 'Your circuit data may still be saved. Reload the page to continue working.';

      const btn = document.createElement('button');
      btn.style.cssText = [
        'padding: 10px 24px',
        'background-color: var(--primary, #2563eb)',
        'color: var(--primary-foreground, #fff)',
        'border: none',
        'border-radius: var(--radius, 8px)',
        'cursor: pointer',
        'font-size: 14px',
        'font-weight: 600'
      ].join('; ');
      btn.textContent = 'Reload Page';
      btn.onclick = function () {
        window.location.reload();
      };

      overlay.appendChild(icon);
      overlay.appendChild(title);
      overlay.appendChild(subtitle);
      overlay.appendChild(btn);

      // Only add if not already present
      if (!document.getElementById('lab-crash-overlay')) {
        labDiv.style.position = 'relative';
        labDiv.appendChild(overlay);
      }
    }

    /**
     * Check if the canvas element exists and has valid dimensions
     */
    function getCanvas() {
      return labDiv.querySelector('canvas');
    }

    // --- Strategy 1: window.onerror ---
    prevErrorHandlerRef.current = window.onerror;
    window.onerror = function (message, source, lineno, colno, error) {
      // Check if this is a Haxe/OpenFL error (from the lab JS bundle)
      const isLabError = (source && (
        source.includes('DCACLab') ||
        source.includes('openfl') ||
        source.includes('lime') ||
        source.includes('haxe')
      )) || (message && (
        typeof message === 'string' && (
          message.includes('clientside') ||
          message.includes('openfl') ||
          message.includes('lime')
        )
      ));

      if (isLabError) {
        console.error('[LabCanvasGuard] Haxe engine error:', message, source, lineno, colno);
        canvasError('window.onerror', { message: message, source: source, line: lineno, col: colno });
        // Check if canvas is still alive after a short delay
        setTimeout(function () {
          const canvas = getCanvas();
          if (!canvas || canvas.width === 0 || canvas.height === 0) {
            showCrashOverlay('Haxe engine error: ' + message);
          }
        }, 500);
      }

      // Call previous handler if it existed
      if (typeof prevErrorHandlerRef.current === 'function') {
        return prevErrorHandlerRef.current(message, source, lineno, colno, error);
      }
      return false;
    };

    // --- Strategy 2: unhandledrejection ---
    prevRejectionHandlerRef.current = window.onunhandledrejection;
    function handleRejection(event) {
      const reason = event.reason;
      const reasonStr = reason ? (reason.message || String(reason)) : 'Unknown';

      if (reasonStr.includes('clientside') || reasonStr.includes('openfl') || reasonStr.includes('lime')) {
        console.error('[LabCanvasGuard] Unhandled rejection from lab engine:', reasonStr);
        canvasError('unhandledrejection', { reason: reasonStr });
        setTimeout(function () {
          const canvas = getCanvas();
          if (!canvas || canvas.width === 0 || canvas.height === 0) {
            showCrashOverlay('Unhandled rejection: ' + reasonStr);
          }
        }, 500);
      }
    }
    window.addEventListener('unhandledrejection', handleRejection);

    // --- Strategy 3: MutationObserver ---
    const canvas = getCanvas();
    if (canvas) {
      observerRef.current = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var mutation = mutations[i];
          for (var j = 0; j < mutation.removedNodes.length; j++) {
            var node = mutation.removedNodes[j];
            if (node.tagName === 'CANVAS' || (node.querySelector && node.querySelector('canvas'))) {
              console.error('[LabCanvasGuard] Canvas element was removed from DOM');
              canvasError('MutationObserver', { event: 'canvas_removed' });
              // Verify it's truly gone (not just a re-render)
              setTimeout(function () {
                if (!getCanvas()) {
                  showCrashOverlay('Canvas element was removed from DOM');
                }
              }, 1000);
            }
          }
        }
      });

      observerRef.current.observe(labDiv, { childList: true, subtree: true });
    }

    // --- Strategy 4: Periodic health check ---
    healthIntervalRef.current = setInterval(function () {
      // Skip when lab container is hidden (e.g. react-lab-holder display:none on school-admin pages).
      // Note: relies on offsetParent, which is null for display:none ancestors. Will not work
      // correctly if lab-div-id ever becomes position:fixed (offsetParent is always null for fixed).
      if (!labDiv.offsetParent) return;

      // Skip when inside react-lab-holder (school-admin assignment editing).
      // Canvas is preloaded with 0x0 dimensions by design.
      if (labDiv.closest('react-lab-holder')) return;

      var currentCanvas = getCanvas();
      if (!currentCanvas) {
        console.error('[LabCanvasGuard] Health check: canvas not found');
        canvasError('health-check', { event: 'canvas_not_found' });
        showCrashOverlay('Canvas element not found during health check');
        return;
      }

      // Check if canvas has been zeroed out (sign of crash)
      if (currentCanvas.width === 0 || currentCanvas.height === 0) {
        console.error('[LabCanvasGuard] Health check: canvas has zero dimensions');
        canvasError('health-check', { event: 'canvas_zero_dimensions', width: currentCanvas.width, height: currentCanvas.height });
        showCrashOverlay('Canvas has zero dimensions');
      }
    }, 5000);

    // --- Cleanup ---
    return function () {
      window.onerror = prevErrorHandlerRef.current;
      window.removeEventListener('unhandledrejection', handleRejection);

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (healthIntervalRef.current) {
        clearInterval(healthIntervalRef.current);
        healthIntervalRef.current = null;
      }

      // Remove overlay if present
      var overlay = document.getElementById('lab-crash-overlay');
      if (overlay && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    };
  }, [isLabLoaded]);
}
